
import React, { useState, useEffect } from 'react';
import { AppState, UserRole, User } from '../types.ts';
import { supabase } from '../lib/supabase.ts';
import { Wallet, QrCode, Key, Clock, UserPlus, Search, ShieldCheck, AlertCircle, Check, Save, ChevronDown, Plus, Zap } from 'lucide-react';

interface FinancialViewProps {
    state: AppState;
}

const FinancialView: React.FC<FinancialViewProps> = ({ state }) => {
    // Plan Settings
    const [prices, setPrices] = useState({ monthly: '', quarterly: '', annual: '' });
    const [pixKeys, setPixKeys] = useState({ monthly: '', quarterly: '', annual: '' });
    const [qrCodes, setQrCodes] = useState({ monthly: '', quarterly: '', annual: '' });

    const [isLoading, setIsLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
    const [searchTerm, setSearchTerm] = useState('');

    // Extension State
    const [selectedPlan, setSelectedPlan] = useState<Record<string, 'monthly' | 'quarterly' | 'annual'>>({});
    const [submissions, setSubmissions] = useState<any[]>([]);

    useEffect(() => {
        fetchSettings();
        fetchSubmissions();
    }, []);

    const fetchSubmissions = async () => {
        const { data } = await supabase.from('payment_submissions').select('*').eq('status', 'pending');
        if (data) setSubmissions(data);
    };

    const fetchSettings = async () => {
        try {
            const { data, error } = await supabase.from('system_settings').select('*').eq('id', 'config').maybeSingle();
            if (data) {
                setPrices({
                    monthly: data.price_monthly?.toString() || '',
                    quarterly: data.price_quarterly?.toString() || '',
                    annual: data.price_annual?.toString() || ''
                });
                setPixKeys({
                    monthly: data.pix_key_monthly || data.pix_key || '',
                    quarterly: data.pix_key_quarterly || '',
                    annual: data.pix_key_annual || ''
                });
                setQrCodes({
                    monthly: data.qr_code_url_monthly || data.qr_code_url || '',
                    quarterly: data.qr_code_url_quarterly || '',
                    annual: data.qr_code_url_annual || ''
                });
            }
        } catch (err) {
            console.error('Erro ao buscar configurações:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setSaveStatus('saving');
        try {
            const { error } = await supabase.from('system_settings').upsert({
                id: 'config',
                price_monthly: parseFloat(prices.monthly) || 0,
                price_quarterly: parseFloat(prices.quarterly) || 0,
                price_annual: parseFloat(prices.annual) || 0,
                pix_key_monthly: pixKeys.monthly,
                pix_key_quarterly: pixKeys.quarterly,
                pix_key_annual: pixKeys.annual,
                qr_code_url_monthly: qrCodes.monthly,
                qr_code_url_quarterly: qrCodes.quarterly,
                qr_code_url_annual: qrCodes.annual,
                updated_at: new Date().toISOString()
            });

            if (error) throw error;
            setSaveStatus('success');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (err) {
            console.error('Erro ao salvar:', err);
            setSaveStatus('error');
        }
    };

    const handleExtendAccess = async (userId: string) => {
        const type = selectedPlan[userId] || 'monthly';
        const days = type === 'monthly' ? 30 : type === 'quarterly' ? 90 : 365;
        const user = state.users.find(u => u.id === userId);
        if (!user) return;

        const currentExpiry = user.subscription_expires_at ? new Date(user.subscription_expires_at) : new Date();
        const newExpiry = new Date(Math.max(currentExpiry.getTime(), Date.now()) + days * 24 * 60 * 60 * 1000);

        const { error } = await supabase.from('profiles').update({
            subscription_expires_at: newExpiry.toISOString()
        }).eq('id', userId);

        if (error) {
            alert('Erro ao estender acesso: ' + error.message);
        } else {
            // Se houver submissão pendente, marcar como aprovada
            const submission = submissions.find(s => s.user_id === userId);
            if (submission) {
                await supabase.from('payment_submissions')
                    .update({ status: 'approved' })
                    .eq('id', submission.id);
                fetchSubmissions();
            }
            alert(`Acesso estendido para ${user.name} por ${days} dias!`);
        }
    };

    const filteredUsers = state.users.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (expiry: string | null | undefined) => {
        if (!expiry) return 'text-[#f6465d]';
        const expDate = new Date(expiry);
        if (expDate < new Date()) return 'text-[#f6465d]';
        const dayDiff = (expDate.getTime() - Date.now()) / (1000 * 3600 * 24);
        if (dayDiff < 7) return 'text-[#fbbf24]';
        return 'text-[#00ff66]';
    };

    if (isLoading) return <div className="p-10 text-center text-[#00ff66] animate-pulse uppercase font-black tracking-widest text-xs">Carregando Financeiro...</div>;

    return (
        <div className="space-y-10 pb-20">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-3xl font-black uppercase tracking-tight text-[#eaecef]">Gestão Financeira</h2>
                    <p className="text-white text-base mt-1 font-bold opacity-90 uppercase tracking-widest text-[10px]">Configurações de Pagamento & Assinaturas</p>
                </div>
                <button
                    onClick={handleSaveSettings}
                    disabled={saveStatus === 'saving'}
                    className={`px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${saveStatus === 'saving' ? 'bg-[#2b3139] text-white/40' :
                        saveStatus === 'success' ? 'bg-[#00ff66] text-[#0b0e11]' :
                            saveStatus === 'error' ? 'bg-[#f6465d] text-white' :
                                'bg-[#00ff66] text-[#0b0e11] hover:shadow-[0_0_25px_rgba(0,255,102,0.3)] shadow-lg'
                        }`}
                >
                    {saveStatus === 'saving' ? 'Salvando...' :
                        saveStatus === 'success' ? <><Check size={18} /> Salvo!</> :
                            saveStatus === 'error' ? <><AlertCircle size={18} /> Erro</> :
                                <><Save size={18} /> Salvar Tudo</>}
                </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {[
                    { key: 'monthly', label: 'Mensal (30 Dias)', color: 'text-[#00ff66]', icon: <Clock size={20} className="text-[#00ff66]" /> },
                    { key: 'quarterly', label: 'Trimestral (90 Dias)', color: 'text-[#fbbf24]', icon: <Zap size={20} className="text-[#fbbf24]" /> },
                    { key: 'annual', label: 'Anual (365 Dias)', color: 'text-[#4788ff]', icon: <ShieldCheck size={20} className="text-[#4788ff]" /> }
                ].map(plan => (
                    <section key={plan.key} className="bg-[#1e2329] rounded-[32px] border border-[#474d57] overflow-hidden shadow-2xl flex flex-col group hover:border-white/20 transition-all">
                        <div className="p-6 border-b border-[#474d57] bg-gradient-to-r from-[#2b3139]/30 to-transparent flex items-center gap-3">
                            {plan.icon}
                            <h3 className="text-sm font-black uppercase tracking-widest text-white">{plan.label}</h3>
                        </div>

                        <div className="p-6 space-y-5">
                            <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-white/40 ml-1">Valor do Plano ($)</label>
                                <input
                                    type="number"
                                    value={prices[plan.key as keyof typeof prices]}
                                    onChange={e => setPrices({ ...prices, [plan.key]: e.target.value })}
                                    className="w-full bg-[#0b0e11] border border-[#474d57] rounded-xl px-4 py-3 text-sm text-white font-black outline-none focus:border-[#00ff66] transition-all"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-white/40 ml-1">Chave PIX Única</label>
                                <div className="relative">
                                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                                    <input
                                        type="text"
                                        value={pixKeys[plan.key as keyof typeof pixKeys]}
                                        onChange={e => setPixKeys({ ...pixKeys, [plan.key]: e.target.value })}
                                        placeholder="Chave Pix para este plano"
                                        className="w-full bg-[#0b0e11] border border-[#474d57] rounded-xl pl-9 pr-4 py-3 text-[11px] text-white font-black outline-none focus:border-[#00ff66] transition-all placeholder:text-white/10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-white/40 ml-1">QR Code Link (Imagem)</label>
                                <div className="relative">
                                    <QrCode className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                                    <input
                                        type="text"
                                        value={qrCodes[plan.key as keyof typeof qrCodes]}
                                        onChange={e => setQrCodes({ ...qrCodes, [plan.key]: e.target.value })}
                                        placeholder="https://..."
                                        className="w-full bg-[#0b0e11] border border-[#474d57] rounded-xl pl-9 pr-4 py-3 text-[11px] text-white font-black outline-none focus:border-[#00ff66] transition-all placeholder:text-white/10"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            <section className="bg-[#1e2329] rounded-[32px] border border-[#474d57] overflow-hidden shadow-2xl flex flex-col">
                <div className="p-8 border-b border-[#474d57] bg-gradient-to-r from-[#2b3139]/30 to-transparent flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-3">
                        <UserPlus className="text-[#00ff66]" size={24} />
                        <h3 className="text-xl font-black uppercase tracking-tight text-white">Controle de Assinaturas</h3>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                        <input
                            type="text"
                            placeholder="BUSCAR NOME OU E-MAIL..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-11 pr-4 py-3.5 text-[11px] text-white font-black outline-none focus:border-[#00ff66] transition-all placeholder:text-white/20"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-[10px] font-black uppercase tracking-[0.2em] text-white/30 sticky top-0 bg-[#1e2329] z-10">
                            <tr>
                                <th className="p-8 border-b border-[#474d57]/30">ID / Usuário</th>
                                <th className="p-8 border-b border-[#474d57]/30">Vencimento Atual</th>
                                <th className="p-8 border-b border-[#474d57]/30">Ação de Renovação</th>
                                <th className="p-8 border-b border-[#474d57]/30 text-right">Confirmar</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#474d57]/20 uppercase">
                            {filteredUsers.map(user => (
                                <tr key={user.id} className="hover:bg-[#2b3139]/40 transition-colors group">
                                    <td className="p-8">
                                        <p className="text-white font-black text-sm group-hover:text-[#00ff66] transition-colors flex items-center gap-2">
                                            {user.name}
                                            {submissions.some(s => s.user_id === user.id) && (
                                                <span className="bg-[#fbbf24] text-[#0b0e11] text-[8px] font-black px-2 py-0.5 rounded-full animate-bounce">PAGAMENTO NOTIFICADO</span>
                                            )}
                                        </p>
                                        <p className="text-[10px] font-bold text-white/40 mt-1 uppercase">
                                            {user.email || 'SEM E-MAIL'}
                                            {submissions.find(s => s.user_id === user.id) && (
                                                <span className="text-[#fbbf24] ml-2">• PLANO {submissions.find(s => s.user_id === user.id).plan_type}</span>
                                            )}
                                        </p>
                                    </td>
                                    <td className="p-8">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${getStatusColor(user.subscription_expires_at).replace('text-', 'bg-')} shadow-[0_0_10px_currentColor]`}></div>
                                            <p className={`font-mono font-black text-xs ${getStatusColor(user.subscription_expires_at)}`}>
                                                {user.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString() : 'SEM ACESSO'}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="p-8">
                                        <div className="relative min-w-[180px]">
                                            <select
                                                value={selectedPlan[user.id] || submissions.find(s => s.user_id === user.id)?.plan_type || 'monthly'}
                                                onChange={e => setSelectedPlan({ ...selectedPlan, [user.id]: e.target.value as any })}
                                                className="w-full bg-[#0b0e11] border border-[#474d57] rounded-xl px-4 py-3 text-[10px] font-black text-white outline-none appearance-none cursor-pointer focus:border-[#fbbf24] transition-all"
                                            >
                                                <option value="monthly">PLANO MENSAL (30D)</option>
                                                <option value="quarterly">PLANO TRIMESTRAL (90D)</option>
                                                <option value="annual">PLANO ANUAL (365D)</option>
                                            </select>
                                            <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                                        </div>
                                    </td>
                                    <td className="p-8 text-right">
                                        <button
                                            onClick={() => handleExtendAccess(user.id)}
                                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 text-white border border-white/10 text-[10px] font-black uppercase tracking-widest hover:bg-[#00ff66] hover:text-[#0b0e11] hover:border-[#00ff66] transition-all transform active:scale-95 group-hover:shadow-[0_0_20px_rgba(0,255,102,0.2)]"
                                        >
                                            <Plus size={14} /> Liberar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredUsers.length === 0 && (
                        <div className="p-20 text-center">
                            <p className="text-white/20 font-black uppercase text-xs tracking-widest">Nenhum usuário encontrado</p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default FinancialView;
